import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Gavel,
  Description,
  TrendingUp,
  OpenInNew,
  Download,
  Assessment,
  Business,
  Security,
  Timeline
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';

interface RegulatorySource {
  id: string;
  name: string;
  country: string;
  authority_type: string;
  website_url: string;
  api_endpoint: string;
}

interface RegulatoryDocument {
  id: string;
  title: string;
  document_type: string;
  document_number: string;
  publication_date: string;
  effective_date: string;
  summary: string;
  document_url: string;
  source_id: string;
  is_processed: boolean;
}

interface RegulatoryChange {
  id: string;
  title: string;
  description: string;
  source: string;
  change_type: string;
  affected_regulation: string;
  change_summary: string;
  impact_level: string;
  affected_products: string[];
  effective_date: string;
  compliance_deadline: string;
}

interface ComplianceStatus {
  compliance_counts: Array<{ status: string; count: number }>;
  upcoming_deadlines_30_days: number;
  overdue_items: number;
}

interface RegulatorySummary {
  total_sources: number;
  total_documents: number;
  total_changes: number;
  total_fund_impacts: number;
  action_required_count: number;
  compliance_rate: number;
}

const RegulatoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [sources, setSources] = useState<RegulatorySource[]>([]);
  const [documents, setDocuments] = useState<RegulatoryDocument[]>([]);
  const [changes, setChanges] = useState<RegulatoryChange[]>([]);
  const [complianceStatus, setComplianceStatus] = useState<ComplianceStatus | null>(null);
  const [summary, setSummary] = useState<RegulatorySummary | null>(null);
  
  // Dialog states
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<RegulatoryDocument | null>(null);
  const [selectedChange, setSelectedChange] = useState<RegulatoryChange | null>(null);

  // Load all regulatory data
  useEffect(() => {
    loadRegulatoryData();
  }, []);

  const loadRegulatoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel using the API service
      const [sourcesRes, documentsRes, changesRes, complianceRes, summaryRes] = await Promise.all([
        api.get('/regulatory/sources'),
        api.get('/regulatory/documents'),
        api.get('/regulatory/changes'),
        api.get('/regulatory/compliance-status'),
        api.get('/regulatory/stats/summary')
      ]);

      setSources(sourcesRes.data.sources || []);
      setDocuments(documentsRes.data.documents || []);
      setChanges(changesRes.data.changes || []);
      setComplianceStatus(complianceRes.data);
      setSummary(summaryRes.data);

    } catch (err) {
      LoggerService.error('Error loading regulatory data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load regulatory data');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (document: RegulatoryDocument) => {
    setSelectedDocument(document);
    setDocumentDialogOpen(true);
  };

  const handleChangeClick = (change: RegulatoryChange) => {
    setSelectedChange(change);
    setChangeDialogOpen(true);
  };

  const getImpactLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'new': return <TrendingUp />;
      case 'amendment': return <Description />;
      case 'clarification': return <Assessment />;
      default: return <Gavel />;
    }
  };

  // Chart data for compliance trends
  const complianceChartData = [
    { month: 'Jan', compliant: 85, actionRequired: 15 },
    { month: 'Feb', compliant: 88, actionRequired: 12 },
    { month: 'Mar', compliant: 82, actionRequired: 18 },
    { month: 'Apr', compliant: 90, actionRequired: 10 },
    { month: 'May', compliant: 87, actionRequired: 13 },
    { month: 'Jun', compliant: 92, actionRequired: 8 }
  ];

  // Chart data for impact levels
  const impactLevelData = [
    { name: 'Critical', value: changes.filter(c => c.impact_level === 'critical').length, color: '#f44336' },
    { name: 'High', value: changes.filter(c => c.impact_level === 'high').length, color: '#ff9800' },
    { name: 'Medium', value: changes.filter(c => c.impact_level === 'medium').length, color: '#2196f3' },
    { name: 'Low', value: changes.filter(c => c.impact_level === 'low').length, color: '#4caf50' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Regulatory Monitor
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadRegulatoryData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Regulatory Monitor
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{summary?.total_sources || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Regulatory Sources
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Description color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{summary?.total_documents || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documents
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{summary?.total_changes || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Regulatory Changes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{summary?.compliance_rate ? `${summary.compliance_rate}%` : '0%'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compliance Status Alert */}
      {complianceStatus && (
        <Alert 
          severity={complianceStatus.overdue_items > 0 ? "error" : complianceStatus.upcoming_deadlines_30_days > 0 ? "warning" : "success"}
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" gutterBottom>
            Compliance Status Overview
          </Typography>
          <Typography variant="body2">
            {complianceStatus.overdue_items > 0 && (
              <>⚠️ {complianceStatus.overdue_items} overdue items require immediate attention. </>
            )}
            {complianceStatus.upcoming_deadlines_30_days > 0 && (
              <>📅 {complianceStatus.upcoming_deadlines_30_days} deadlines approaching within 30 days. </>
            )}
            {complianceStatus.overdue_items === 0 && complianceStatus.upcoming_deadlines_30_days === 0 && (
              <>✅ All compliance deadlines are met. </>
            )}
          </Typography>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" icon={<Assessment />} />
          <Tab label="Regulatory Sources" icon={<Business />} />
          <Tab label="Documents" icon={<Description />} />
          <Tab label="Changes" icon={<TrendingUp />} />
          <Tab label="Compliance Analytics" icon={<Timeline />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Compliance Chart */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Compliance Trends" />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={complianceChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="compliant" stroke="#4caf50" name="Compliant" />
                    <Line type="monotone" dataKey="actionRequired" stroke="#ff9800" name="Action Required" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Impact Level Distribution */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Impact Level Distribution" />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={impactLevelData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {impactLevelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Changes */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Recent Regulatory Changes" />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Impact Level</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changes.slice(0, 5).map((change) => (
                        <TableRow key={change.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {change.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {change.change_summary}
                            </Typography>
                          </TableCell>
                          <TableCell>{change.source}</TableCell>
                          <TableCell>
                            <Chip 
                              icon={getChangeTypeIcon(change.change_type)}
                              label={change.change_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={change.impact_level}
                              color={getImpactLevelColor(change.impact_level) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{new Date(change.effective_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              onClick={() => handleChangeClick(change)}
                            >
                              <OpenInNew />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card>
          <CardHeader title="Regulatory Sources" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Website</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map((source) => (
                    <TableRow key={source.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {source.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{source.country}</TableCell>
                      <TableCell>
                        <Chip 
                          label={source.authority_type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => window.open(source.website_url, '_blank')}
                        >
                          <OpenInNew />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View API Endpoint">
                          <IconButton size="small">
                            <Security />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardHeader title="Regulatory Documents" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Document Number</TableCell>
                    <TableCell>Publication Date</TableCell>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((document) => (
                    <TableRow key={document.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {document.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {document.summary}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={document.document_type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{document.document_number}</TableCell>
                      <TableCell>{new Date(document.publication_date).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(document.effective_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip 
                          label={document.is_processed ? "Processed" : "Pending"}
                          color={document.is_processed ? "success" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDocumentClick(document)}
                        >
                          <OpenInNew />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => window.open(document.document_url, '_blank')}
                        >
                          <Download />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <Card>
          <CardHeader title="Regulatory Changes" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Impact Level</TableCell>
                    <TableCell>Affected Products</TableCell>
                    <TableCell>Compliance Deadline</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((change) => (
                    <TableRow key={change.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {change.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {change.description}
                        </Typography>
                      </TableCell>
                      <TableCell>{change.source}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={getChangeTypeIcon(change.change_type)}
                          label={change.change_type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={change.impact_level}
                          color={getImpactLevelColor(change.impact_level) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {change.affected_products?.map((product, index) => (
                          <Chip 
                            key={index}
                            label={product}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {change.compliance_deadline && (
                          <Typography 
                            variant="body2"
                            color={new Date(change.compliance_deadline) < new Date() ? "error" : "text.primary"}
                          >
                            {new Date(change.compliance_deadline).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleChangeClick(change)}
                        >
                          <OpenInNew />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Grid container spacing={3}>
          {/* Compliance Status Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Compliance Status Breakdown" />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={complianceStatus?.compliance_counts || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#2196f3" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Upcoming Deadlines */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Upcoming Deadlines" />
              <CardContent>
                <Typography variant="h4" color="warning.main" gutterBottom>
                  {complianceStatus?.upcoming_deadlines_30_days || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Deadlines in next 30 days
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(100, ((complianceStatus?.upcoming_deadlines_30_days || 0) / 20) * 100)} 
                  sx={{ mb: 2 }}
                />
                
                <Typography variant="h4" color="error.main" gutterBottom>
                  {complianceStatus?.overdue_items || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overdue items
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Document Detail Dialog */}
      <Dialog open={documentDialogOpen} onClose={() => setDocumentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{selectedDocument?.title}</Typography>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Document Type</Typography>
                  <Typography variant="body1">{selectedDocument.document_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Document Number</Typography>
                  <Typography variant="body1">{selectedDocument.document_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Publication Date</Typography>
                  <Typography variant="body1">{new Date(selectedDocument.publication_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Effective Date</Typography>
                  <Typography variant="body1">{new Date(selectedDocument.effective_date).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Typography variant="body1" paragraph>
                {selectedDocument.summary}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  startIcon={<Download />}
                  onClick={() => window.open(selectedDocument.document_url, '_blank')}
                >
                  Download Document
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Change Detail Dialog */}
      <Dialog open={changeDialogOpen} onClose={() => setChangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{selectedChange?.title}</Typography>
        </DialogTitle>
        <DialogContent>
          {selectedChange && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Source</Typography>
                  <Typography variant="body1">{selectedChange.source}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Change Type</Typography>
                  <Typography variant="body1">{selectedChange.change_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Impact Level</Typography>
                  <Chip 
                    label={selectedChange.impact_level}
                    color={getImpactLevelColor(selectedChange.impact_level) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Affected Regulation</Typography>
                  <Typography variant="body1">{selectedChange.affected_regulation}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Effective Date</Typography>
                  <Typography variant="body1">{new Date(selectedChange.effective_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Compliance Deadline</Typography>
                  <Typography 
                    variant="body1"
                    color={selectedChange.compliance_deadline && new Date(selectedChange.compliance_deadline) < new Date() ? "error" : "text.primary"}
                  >
                    {selectedChange.compliance_deadline ? new Date(selectedChange.compliance_deadline).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Description</Typography>
              <Typography variant="body1" paragraph>
                {selectedChange.description}
              </Typography>
              
              <Typography variant="h6" gutterBottom>Change Summary</Typography>
              <Typography variant="body1" paragraph>
                {selectedChange.change_summary}
              </Typography>
              
              <Typography variant="h6" gutterBottom>Affected Products</Typography>
              <Box sx={{ mb: 2 }}>
                {selectedChange.affected_products?.map((product, index) => (
                  <Chip 
                    key={index}
                    label={product}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegulatoryPage;
